import moment from "moment";

type LeasingDetails = {
    title: string;
    type: string;
    leasingType: { text: string; value: string };
    startDate: string;
    bobbeeStartDate: string;
    leasingPeriod: number;
    netAmount: number;
    grossAmount: number;
    vatAmount: number;
    vatRate: { text: string; value: number; vatType: string; vatRate: number };
    leasingRate: number;
    insuranceNetAmount: number;
    insuranceVatRate: { value: number; vatType: string; vatRate: number; text: string };
    insuranceGrossAmount: number;
    monthlyLeasingRate: number;
    maintenanceNetAmount: number;
    maintenanceVatRate: { value: number; vatType: string; vatRate: number; text: string };
    maintenanceGrossAmount: number;
    applicationFeeAmount: number;
    leasingPurchaseOption: boolean;
    leasingEarlyRedemption: boolean;
    leasingEarlyRedemptionAmount: number;
    leasingEarlyRedemptionDate: string;
    firstPayRentOption: boolean;
    firstPayRentAmount: number;
    residualAmount: number;
};

type Payment = {
    date: string;
    netAmount: number;
    vatAmount: number;
    grossAmount: number;
    insuranceNetAmount: number;
    insuranceVatAmount: number;
    insuranceGrossAmount: number;
    maintenanceNetAmount: number;
    maintenanceVatAmount: number;
    maintenanceGrossAmount: number;
    totalAmount: number;
};

/**
 * Calculates a regular leasing payment.
 * @param {LeasingDetails} leasing - The leasing details.
 * @param {number} index - The current month index.
 * @returns {Payment} - The payment details for the current month.
 */
const calculateRegularPayment = (leasing: LeasingDetails, index: number): Payment => {
    return {
        date: moment(leasing.startDate).add(index, "months").format("YYYY-MM-DD"),
        netAmount: leasing.netAmount ? +leasing.netAmount : 0,
        vatAmount:
            leasing.netAmount && leasing.grossAmount ? +leasing.grossAmount - leasing.netAmount : 0,
        grossAmount: leasing.grossAmount ? +leasing.grossAmount : 0,
        insuranceNetAmount: leasing.insuranceNetAmount ? +leasing.insuranceNetAmount : 0,
        insuranceVatAmount:
            leasing.insuranceGrossAmount && leasing.insuranceNetAmount
                ? +leasing.insuranceGrossAmount - leasing.insuranceNetAmount
                : 0,
        insuranceGrossAmount: leasing.insuranceGrossAmount ? +leasing.insuranceGrossAmount : 0,
        maintenanceNetAmount: leasing.maintenanceNetAmount ? +leasing.maintenanceNetAmount : 0,
        maintenanceVatAmount:
            leasing.maintenanceGrossAmount && leasing.maintenanceNetAmount
                ? +leasing.maintenanceGrossAmount - leasing.maintenanceNetAmount
                : 0,
        maintenanceGrossAmount: leasing.maintenanceGrossAmount
            ? +leasing.maintenanceGrossAmount
            : 0,
        totalAmount:
            +leasing.grossAmount +
            (leasing.insuranceGrossAmount ? +leasing.insuranceGrossAmount : 0) +
            (leasing.maintenanceGrossAmount ? +leasing.maintenanceGrossAmount : 0),
    };
};

/**
 * Calculates the first payment with different rent amount.
 * @param {LeasingDetails} leasing - The leasing details.
 * @returns {Payment} - The first payment details.
 */
const calculateFirstPayment = (leasing: LeasingDetails): Payment => {
    return {
        date: moment(leasing.startDate).format("YYYY-MM-DD"),
        netAmount: +leasing.firstPayRentAmount,
        vatAmount: 0,
        grossAmount: +leasing.firstPayRentAmount,
        insuranceNetAmount: 0,
        insuranceVatAmount: 0,
        insuranceGrossAmount: 0,
        maintenanceNetAmount: 0,
        maintenanceVatAmount: 0,
        maintenanceGrossAmount: 0,
        totalAmount:
            +leasing.firstPayRentAmount +
            (leasing.applicationFeeAmount ? +leasing.applicationFeeAmount : 0),
    };
};

/**
 * Calculates the application fee payment.
 * @param {LeasingDetails} leasing - The leasing details.
 * @param {number} index - The current month index.
 * @returns {Payment} - The application fee payment details.
 */
const calculateApplicationFeePayment = (leasing: LeasingDetails, index: number): Payment => {
    const netAmountApplicationFee = +(leasing.netAmount + leasing.applicationFeeAmount);
    const vatAmountApplicationFee =
        +(leasing.netAmount + leasing.applicationFeeAmount) * (leasing.vatRate.vatRate / 100);
    const grossAmountApplicationFee = netAmountApplicationFee + vatAmountApplicationFee;

    return {
        date: moment(leasing.startDate).add(index, "months").format("YYYY-MM-DD"),
        netAmount: netAmountApplicationFee ? +netAmountApplicationFee : 0,
        vatAmount: vatAmountApplicationFee ? +vatAmountApplicationFee : 0,
        grossAmount: grossAmountApplicationFee ? +grossAmountApplicationFee : 0,
        insuranceNetAmount: leasing.insuranceNetAmount ? +leasing.insuranceNetAmount : 0,
        insuranceVatAmount:
            leasing.insuranceGrossAmount && leasing.insuranceNetAmount
                ? +leasing.insuranceGrossAmount - leasing.insuranceNetAmount
                : 0,
        insuranceGrossAmount: leasing.insuranceGrossAmount ? +leasing.insuranceGrossAmount : 0,
        maintenanceNetAmount: leasing.maintenanceNetAmount ? +leasing.maintenanceNetAmount : 0,
        maintenanceVatAmount:
            leasing.maintenanceGrossAmount && leasing.maintenanceNetAmount
                ? +leasing.maintenanceGrossAmount - leasing.maintenanceNetAmount
                : 0,
        maintenanceGrossAmount: leasing.maintenanceGrossAmount
            ? +leasing.maintenanceGrossAmount
            : 0,
        totalAmount:
            +grossAmountApplicationFee +
            (leasing.insuranceGrossAmount ? +leasing.insuranceGrossAmount : 0) +
            (leasing.maintenanceGrossAmount ? +leasing.maintenanceGrossAmount : 0),
    };
};

/**
 * Calculates the residual payment.
 * @param {LeasingDetails} leasing - The leasing details.
 * @param {number} index - The current month index.
 * @returns {Payment} - The residual payment details.
 */
const calculateResidualPayment = (leasing: LeasingDetails, index: number): Payment => {
    return {
        date: moment(leasing.startDate).add(index, "months").format("YYYY-MM-DD"),
        netAmount: leasing.residualAmount,
        vatAmount: 0,
        grossAmount: leasing.residualAmount,
        insuranceNetAmount: 0,
        insuranceVatAmount: 0,
        insuranceGrossAmount: 0,
        maintenanceNetAmount: 0,
        maintenanceVatAmount: 0,
        maintenanceGrossAmount: 0,
        totalAmount: leasing.residualAmount,
    };
};

/**
 * Checks if a date is after the early redemption date.
 * @param {string} date - The date to check.
 * @param {string} earlyRedemptionDate - The early redemption date.
 * @returns {boolean} - True if the date is after the early redemption date, false otherwise.
 */
const isAfterEarlyRedemption = (date: string, earlyRedemptionDate: string): boolean => {
    return moment(date).isAfter(moment(earlyRedemptionDate));
};

/**
 * Calculates the leasing payment schedule.
 * @param {LeasingDetails} leasing - The leasing details.
 * @returns {Payment[]} - The leasing payment schedule.
 */
export const calculateLeasing = (leasing: LeasingDetails): Payment[] => {
    if (!leasing) {
        return [];
    }

    const payments: Payment[] = [];

    if (leasing.grossAmount && leasing.netAmount && leasing.startDate && leasing.leasingPeriod) {
        for (let i = 0; i < leasing.leasingPeriod; i++) {
            let payment = calculateRegularPayment(leasing, i);

            // Handle first payment with different rent amount
            if (leasing.firstPayRentOption && leasing.firstPayRentAmount && i === 0) {
                payment = calculateFirstPayment(leasing);
            }

            // Handle application fee payment
            if (leasing.applicationFeeAmount && i === 0) {
                payment = calculateApplicationFeePayment(leasing, i);
            }

            // Handle residual payment
            if (leasing.residualAmount && i === leasing.leasingPeriod - 1) {
                payment = calculateResidualPayment(leasing, i);
            }

            // Handle early redemption
            if (leasing.leasingEarlyRedemption && leasing.leasingEarlyRedemptionDate) {
                if (isAfterEarlyRedemption(payment.date, leasing.leasingEarlyRedemptionDate)) {
                    break;
                }
            }

            payments.push(payment);
        }
    }

    return payments;
};
